import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useState, useEffect } from "react";
import { observer } from "mobx-react";
import { Form, Spin, Input, Select, Radio, App, Upload, Switch } from "antd";
import { __, request } from "../../../utils";
import { useStores } from "../../../store";
import { locationRestImportPost } from "../../../wp-api";
import { ImportFormResultMessage } from ".";
import { UploadOutlined } from "@ant-design/icons";
const LAYOUT = {
    labelCol: {
        span: 24
    },
    wrapperCol: {
        span: 24
    }
};
const ImportForm = observer(()=>{
    var _result_messages;
    const { message } = App.useApp();
    const { cookieStore, optionStore } = useStores();
    const { groups, busy } = cookieStore;
    const [form] = Form.useForm();
    const [isBusy, setBusy] = useState(false);
    const [result, setResult] = useState(undefined);
    const initialValues = {
        json: "",
        cookieStatus: "keep",
        cookieSkipExisting: true,
        blockerStatus: "keep",
        blockerSkipExisting: true,
        tcfVendorConfigurationStatus: "keep"
    };
    // Initially load all available cookie groups
    useEffect(()=>{
        cookieStore.fetchGroups();
    }, []);
    // Request export data from REST API
    const handleFinish = useCallback(async (values)=>{
        setBusy(true);
        try {
            setResult(await request({
                location: locationRestImportPost,
                request: {
                    cookieGroup: 0,
                    cookieStatus: "keep",
                    cookieSkipExisting: true,
                    blockerStatus: "keep",
                    blockerSkipExisting: true,
                    tcfVendorConfigurationStatus: "keep",
                    ...values
                }
            }));
            optionStore.fetchCurrentRevision();
            optionStore.fetchSettings();
            optionStore.fetchBannerLinks();
            message.success(__("Successfully imported!"));
        } catch (e) {
            message.error(e.responseJSON.message);
        } finally{
            setBusy(false);
        }
    }, []);
    // Upload is currently not a real upload, instead the content is read and put into the textarea
    const handleUploadFile = useCallback((file)=>{
        const reader = new FileReader();
        reader.readAsText(file, "UTF-8");
        reader.onload = (evt)=>form.setFieldsValue({
                json: evt.target.result
            });
        reader.onerror = ()=>form.setFieldsValue({
                json: __("File could not be read.")
            });
        return false;
    }, [
        form
    ]);
    // Check if a given JSON is valid and has a first level key
    const hasValidJson = useCallback((json, keyToCheck)=>{
        try {
            const obj = JSON.parse(json);
            return keyToCheck ? !!obj[keyToCheck] : true;
        } catch (e) {
            return false;
        }
    }, []);
    return /*#__PURE__*/ _jsx(Spin, {
        spinning: isBusy || busy,
        children: /*#__PURE__*/ _jsxs(Form, {
            name: "import",
            form: form,
            ...LAYOUT,
            initialValues: initialValues,
            onFinish: handleFinish,
            labelWrap: true,
            children: [
                /*#__PURE__*/ _jsxs(Form.Item, {
                    label: __("Content to import"),
                    required: true,
                    children: [
                        /*#__PURE__*/ _jsx(Form.Item, {
                            name: "json",
                            rules: [
                                {
                                    required: true,
                                    message: __("Please provide a value!")
                                }
                            ],
                            extra: __("You can get the settings as JSON file or text if you export the settings in this or another WordPress installation."),
                            children: /*#__PURE__*/ _jsx(Input.TextArea, {
                                rows: 5
                            })
                        }),
                        /*#__PURE__*/ _jsx("p", {
                            className: "description",
                            children: __("or select a file to upload:")
                        }),
                        /*#__PURE__*/ _jsx(Upload, {
                            accept: "application/json",
                            showUploadList: false,
                            beforeUpload: handleUploadFile,
                            children: /*#__PURE__*/ _jsxs("a", {
                                className: "button",
                                children: [
                                    /*#__PURE__*/ _jsx(UploadOutlined, {}),
                                    " ",
                                    __("Select File")
                                ]
                            })
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx(Form.Item, {
                    noStyle: true,
                    shouldUpdate: (prevValues, nextValues)=>prevValues.json !== nextValues.json,
                    children: (param)=>{
                        let { getFieldValue } = param;
                        return hasValidJson(getFieldValue("json"), "cookies") && /*#__PURE__*/ _jsxs(Form.Item, {
                            label: __("Fallback service group"),
                            required: true,
                            children: [
                                /*#__PURE__*/ _jsx(Form.Item, {
                                    name: "cookieGroup",
                                    noStyle: true,
                                    rules: [
                                        {
                                            required: true,
                                            message: __("Please provide a group!")
                                        }
                                    ],
                                    children: /*#__PURE__*/ _jsx(Select, {
                                        style: {
                                            width: "70%"
                                        },
                                        children: groups.sortedGroups.map((param)=>{
                                            let { data: { id, name } } = param;
                                            return /*#__PURE__*/ _jsx(Select.Option, {
                                                value: id,
                                                children: name
                                            }, id);
                                        })
                                    })
                                }),
                                /*#__PURE__*/ _jsx("p", {
                                    className: "description",
                                    children: __("Select an alternative group to assign the service to if an imported service cannot be assigned to its original group.")
                                })
                            ]
                        });
                    }
                }),
                /*#__PURE__*/ _jsx(Form.Item, {
                    noStyle: true,
                    shouldUpdate: (prevValues, nextValues)=>prevValues.json !== nextValues.json,
                    children: (param)=>{
                        let { getFieldValue } = param;
                        return hasValidJson(getFieldValue("json"), "cookies") && /*#__PURE__*/ _jsxs(_Fragment, {
                            children: [
                                /*#__PURE__*/ _jsx(Form.Item, {
                                    children: /*#__PURE__*/ _jsxs("span", {
                                        children: [
                                            /*#__PURE__*/ _jsx(Form.Item, {
                                                name: "cookieSkipExisting",
                                                valuePropName: "checked",
                                                noStyle: true,
                                                children: /*#__PURE__*/ _jsx(Switch, {})
                                            }),
                                            /*#__PURE__*/ _jsxs("span", {
                                                children: [
                                                    "\xa0\xa0",
                                                    __("Skip already existing services")
                                                ]
                                            })
                                        ]
                                    })
                                }),
                                /*#__PURE__*/ _jsx(Form.Item, {
                                    label: __("Set service status"),
                                    name: "cookieStatus",
                                    rules: [
                                        {
                                            required: true,
                                            message: __("Please choose a status!")
                                        }
                                    ],
                                    children: /*#__PURE__*/ _jsxs(Radio.Group, {
                                        children: [
                                            /*#__PURE__*/ _jsx(Radio.Button, {
                                                value: "keep",
                                                children: __("Keep")
                                            }),
                                            /*#__PURE__*/ _jsx(Radio.Button, {
                                                value: "publish",
                                                children: __("Enabled")
                                            }),
                                            /*#__PURE__*/ _jsx(Radio.Button, {
                                                value: "private",
                                                children: __("Disabled")
                                            }),
                                            /*#__PURE__*/ _jsx(Radio.Button, {
                                                value: "draft",
                                                children: __("Draft")
                                            })
                                        ]
                                    })
                                })
                            ]
                        });
                    }
                }),
                /*#__PURE__*/ _jsx(Form.Item, {
                    noStyle: true,
                    shouldUpdate: (prevValues, nextValues)=>prevValues.json !== nextValues.json,
                    children: (param)=>{
                        let { getFieldValue } = param;
                        return hasValidJson(getFieldValue("json"), "blocker") && /*#__PURE__*/ _jsxs(_Fragment, {
                            children: [
                                /*#__PURE__*/ _jsx(Form.Item, {
                                    children: /*#__PURE__*/ _jsxs("span", {
                                        children: [
                                            /*#__PURE__*/ _jsx(Form.Item, {
                                                name: "blockerSkipExisting",
                                                valuePropName: "checked",
                                                noStyle: true,
                                                children: /*#__PURE__*/ _jsx(Switch, {})
                                            }),
                                            /*#__PURE__*/ _jsxs("span", {
                                                children: [
                                                    "\xa0\xa0",
                                                    __("Skip already existing content blocker")
                                                ]
                                            })
                                        ]
                                    })
                                }),
                                /*#__PURE__*/ _jsx(Form.Item, {
                                    label: __("Set content blocker status"),
                                    name: "blockerStatus",
                                    rules: [
                                        {
                                            required: true,
                                            message: __("Please choose a status!")
                                        }
                                    ],
                                    children: /*#__PURE__*/ _jsxs(Radio.Group, {
                                        children: [
                                            /*#__PURE__*/ _jsx(Radio.Button, {
                                                value: "keep",
                                                children: __("Keep")
                                            }),
                                            /*#__PURE__*/ _jsx(Radio.Button, {
                                                value: "publish",
                                                children: __("Enabled")
                                            }),
                                            /*#__PURE__*/ _jsx(Radio.Button, {
                                                value: "private",
                                                children: __("Disabled")
                                            }),
                                            /*#__PURE__*/ _jsx(Radio.Button, {
                                                value: "draft",
                                                children: __("Draft")
                                            })
                                        ]
                                    })
                                })
                            ]
                        });
                    }
                }),
                /*#__PURE__*/ _jsx(Form.Item, {
                    noStyle: true,
                    shouldUpdate: (prevValues, nextValues)=>prevValues.json !== nextValues.json,
                    children: (param)=>{
                        let { getFieldValue } = param;
                        return hasValidJson(getFieldValue("json"), "tcfVendorConfigurations") && /*#__PURE__*/ _jsx(Form.Item, {
                            label: __("Set TCF Vendor configuration status"),
                            name: "tcfVendorConfigurationStatus",
                            rules: [
                                {
                                    required: true,
                                    message: __("Please choose a status!")
                                }
                            ],
                            children: /*#__PURE__*/ _jsxs(Radio.Group, {
                                children: [
                                    /*#__PURE__*/ _jsx(Radio.Button, {
                                        value: "keep",
                                        children: __("Keep")
                                    }),
                                    /*#__PURE__*/ _jsx(Radio.Button, {
                                        value: "publish",
                                        children: __("Enabled")
                                    }),
                                    /*#__PURE__*/ _jsx(Radio.Button, {
                                        value: "private",
                                        children: __("Disabled")
                                    }),
                                    /*#__PURE__*/ _jsx(Radio.Button, {
                                        value: "draft",
                                        children: __("Draft")
                                    })
                                ]
                            })
                        });
                    }
                }),
                /*#__PURE__*/ _jsx(Form.Item, {
                    children: /*#__PURE__*/ _jsx("input", {
                        type: "submit",
                        className: "button button-primary",
                        style: {
                            marginTop: 10
                        },
                        value: __("Import")
                    })
                }),
                /*#__PURE__*/ _jsx(Form.Item, {
                    style: {
                        display: (result == null ? void 0 : (_result_messages = result.messages) == null ? void 0 : _result_messages.length) ? "block" : "none"
                    },
                    children: /*#__PURE__*/ _jsx(ImportFormResultMessage, {
                        result: result
                    })
                })
            ]
        })
    });
});
export { ImportForm };
